'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSession } from 'next-auth/react'

import { extractTokenFromClientSession } from 'modules/customer'
import {
  DeliverySlotsByTimeOfTheDay,
  divideTimeSlotsByTimeOfTheDay,
  generateEmptyDeliverySlots,
  generateNext7Days,
  getTMSDeliverySlots,
  parseTMSDeliverySlots,
  TMSDeliveryMethodIdPayload,
  TMSDeliverySlotsArraySlot,
  TMSReservationSlotData
} from 'modules/delivery'
import { getApiErrors } from 'shared/utils/errors'

import { Cart } from '../../../types'

// Types
interface SlotCache {
  [date: string]: {
    rawSlots: TMSDeliverySlotsArraySlot[]
    dividedSlots: DeliverySlotsByTimeOfTheDay
    emptySlots: DeliverySlotsByTimeOfTheDay
  }
}

interface Selection {
  date: string | null
  slots: TMSDeliverySlotsArraySlot[]
}

interface DeliveryDate {
  date: string
  dayOfWeek: string
  shortDate: string
}

interface CoordinatesRef {
  lat?: number
  lng?: number
  postalCode?: string
}

// Hook for managing delivery dates
export const useDeliveryDates = (
  reservationSlotData?: TMSReservationSlotData
) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null)
  const [dates, setDates] = useState<DeliveryDate[]>([])

  useEffect(() => {
    const generatedDates = generateNext7Days()

    setDates(generatedDates)

    const initialDate = reservationSlotData?.slot.date || generatedDates[0].date

    setSelectedDate(initialDate)
  }, [reservationSlotData])

  return { selectedDate, setSelectedDate, dates } as const
}

export const useDeliverySlots = (
  selectedDate: string | null,
  cart: Cart | null,
  reservationSlotData?: TMSReservationSlotData
) => {
  const [rawDeliverySlots, setRawDeliverySlots] = useState<
    TMSDeliverySlotsArraySlot[]
  >([])
  const [deliverySlots, setDeliverySlots] =
    useState<DeliverySlotsByTimeOfTheDay | null>(null)
  const [emptyDeliverySlots, setEmptyDeliverySlots] =
    useState<DeliverySlotsByTimeOfTheDay | null>(null)
  const [selection, setSelection] = useState<Selection>({
    date: null,
    slots: []
  })
  const [previousSelection, setPreviousSelection] = useState<Selection>({
    date: null,
    slots: []
  })
  const [isPristine, setIsPristine] = useState(!!reservationSlotData)
  const [isFetchingSlots, setIsFetchingSlots] = useState(true)

  const cache = useRef<SlotCache>({})
  const currentCartId = useRef<string | null>(null)
  const currentCoordinates = useRef<CoordinatesRef>({})

  const session = useSession()
  const token = useMemo(() => extractTokenFromClientSession(session), [session])

  const getTMSDeliverySlotsByDeliveryMethodId = useCallback(
    async ({ cartId, date }: TMSDeliveryMethodIdPayload) =>
      cartId ? getTMSDeliverySlots({ cartId, date, token }) : null,
    [token]
  )

  // Use ref to prevent function recreation causing re-renders
  const getTMSDeliverySlotsByDeliveryMethodIdRef = useRef(
    getTMSDeliverySlotsByDeliveryMethodId
  )

  getTMSDeliverySlotsByDeliveryMethodIdRef.current =
    getTMSDeliverySlotsByDeliveryMethodId

  const selectedDeliverySlots =
    selectedDate === selection.date ? selection.slots : []

  const previousDeliverySlots =
    selectedDate === previousSelection.date ? previousSelection.slots : []

  const clearCache = useCallback(() => {
    cache.current = {}
  }, [])

  const setSelectedDeliverySlots = useCallback(
    (
      slotsOrUpdater:
        | TMSDeliverySlotsArraySlot[]
        | ((
            prevSlots: TMSDeliverySlotsArraySlot[]
          ) => TMSDeliverySlotsArraySlot[])
    ) => {
      if (isPristine) {
        setIsPristine(false)
      }

      setSelection(prevSelection => {
        const currentSlots =
          selectedDate === prevSelection.date ? prevSelection.slots : []

        const newSlots =
          typeof slotsOrUpdater === 'function'
            ? slotsOrUpdater(currentSlots)
            : slotsOrUpdater

        if (newSlots.length === 0) {
          return { date: null, slots: [] }
        }

        return { date: selectedDate, slots: newSlots }
      })
    },
    [isPristine, selectedDate]
  )

  useEffect(() => {
    const cartChanged = currentCartId.current !== cart?.id
    const coordinatesChanged =
      currentCoordinates.current.lat !== cart?.deliveryAddress?.latitude ||
      currentCoordinates.current.lng !== cart?.deliveryAddress?.longitude ||
      currentCoordinates.current.postalCode !==
        cart?.deliveryAddress?.postalCode

    if (cartChanged || coordinatesChanged) {
      clearCache()
      currentCartId.current = cart?.id || null
      currentCoordinates.current = {
        lat: cart?.deliveryAddress?.latitude || undefined,
        lng: cart?.deliveryAddress?.longitude || undefined,
        postalCode: cart?.deliveryAddress?.postalCode
      }
    }
  }, [
    cart?.id,
    cart?.deliveryAddress?.latitude,
    cart?.deliveryAddress?.longitude,
    cart?.deliveryAddress?.postalCode,
    clearCache
  ])

  useEffect(() => {
    if (!selectedDate) return

    const hasValidCoordinates =
      cart?.deliveryAddress?.latitude && cart?.deliveryAddress?.longitude

    if (!hasValidCoordinates) {
      setIsFetchingSlots(false)

      return
    }

    const cachedData = cache.current[selectedDate]

    if (cachedData) {
      setRawDeliverySlots(cachedData.rawSlots)
      setDeliverySlots(cachedData.dividedSlots)
      setEmptyDeliverySlots(cachedData.emptySlots)
      setIsFetchingSlots(false)

      return
    }
    setIsFetchingSlots(true)
    setDeliverySlots(null)

    const generatedSlots = generateEmptyDeliverySlots(selectedDate)

    setEmptyDeliverySlots(generatedSlots)

    getTMSDeliverySlotsByDeliveryMethodIdRef
      .current({
        cartId: cart?.id || '',
        date: selectedDate,
        postalCode: cart?.deliveryAddress?.postalCode
      })
      .then(resp => {
        const parsedSlots = parseTMSDeliverySlots(
          resp
        )
        const timeSlots = parsedSlots[0].timeSlots || []
        const dividedSlots = divideTimeSlotsByTimeOfTheDay(timeSlots)

        // Cache the results
        cache.current[selectedDate] = {
          rawSlots: timeSlots,
          dividedSlots,
          emptySlots: generatedSlots
        }

        setRawDeliverySlots(timeSlots)
        setDeliverySlots(dividedSlots)
      })
      .catch(err => {
        console.error(getApiErrors(err))
        setDeliverySlots({ morning: [], afternoon: [], evening: [] })
      })
      .finally(() => {
        setIsFetchingSlots(false)
      })
  }, [
    selectedDate,
    cart?.deliveryAddress?.latitude,
    cart?.deliveryAddress?.longitude,
    cart?.id,
    cart?.deliveryAddress?.postalCode
  ])

  useEffect(() => {
    const shouldProcessReservation =
      isPristine &&
      !isFetchingSlots &&
      reservationSlotData &&
      emptyDeliverySlots &&
      reservationSlotData.slot.date === selectedDate

    if (!shouldProcessReservation) return

    const { from, to } = reservationSlotData.slotExtended
    const fromTime = new Date(from).getTime()
    const toTime = new Date(to).getTime()

    const allPossibleSlots = [
      ...emptyDeliverySlots.morning,
      ...emptyDeliverySlots.afternoon,
      ...emptyDeliverySlots.evening
    ]

    const initialSelectedSlots = allPossibleSlots.filter(slot => {
      const [startTimeStr, endTimeStr] = slot.time.split('-').map(s => s.trim())

      if (!startTimeStr || !endTimeStr) return false

      const slotStartDateTime = new Date(
        `${selectedDate}T${startTimeStr}`
      ).getTime()
      const slotEndDateTime = new Date(
        `${selectedDate}T${endTimeStr}`
      ).getTime()

      return slotStartDateTime >= fromTime && slotEndDateTime <= toTime
    })

    // Mark unavailable slots
    const unavailableSlots = initialSelectedSlots.filter(
      initialSlot =>
        !rawDeliverySlots.some(
          availableSlot => availableSlot.time === initialSlot.time
        )
    )

    unavailableSlots.forEach(slot => {
      slot.isUnavailable = true
    })

    setSelection({ date: selectedDate, slots: initialSelectedSlots })
    setPreviousSelection({ date: selectedDate, slots: initialSelectedSlots })
  }, [
    isPristine,
    isFetchingSlots,
    selectedDate,
    reservationSlotData,
    emptyDeliverySlots,
    rawDeliverySlots
  ])

  return {
    rawDeliverySlots,
    deliverySlots,
    emptyDeliverySlots,
    selectedDeliverySlots,
    previousDeliverySlots,
    setSelectedDeliverySlots,
    isFetchingSlots,
    dateWithSelection: selection.date,
    previousDateWithSelection: previousSelection.date,
    allSelectedSlots: selection.slots,
    previousAllSelectedSlots: previousSelection.slots,
    rawSlotsForSelection: selection.date
      ? cache.current[selection.date]?.rawSlots || []
      : [],
    clearCache
  } as const
}

export const useHoverPreview = () => {
  const [hoverPreviewSlots, setHoverPreviewSlots] = useState<{
    slots: TMSDeliverySlotsArraySlot[]
    action: 'add' | 'remove'
  } | null>(null)

  return { hoverPreviewSlots, setHoverPreviewSlots } as const
}
